/**
 * useConversationMessages — server-authoritative conversation history source
 * (brief 011 PR 4/5, push delivery PR 7b).
 *
 * Fetches the whole tree from the server-owned `conversationMessages` query and
 * exposes it as an exhaustive {@link ServerHistoryState} (`loading | ready |
 * unsupported | error`) for the ephemeral-slot render path, replacing the client
 * `useChildPlaybookMessages` fan-out. Because the query returns root + all child
 * sessions in one shot and is refetched wholesale, there is no root-refresh gap and
 * no second history owner. The provider decides what a non-`ready` state renders —
 * this hook never collapses failure/unsupported into empty history.
 *
 * Freshness:
 *   - Fast path: the `conversationHistoryChanged` push subscription nudges a coalesced refetch.
 *   - Tab-return: a hidden→visible edge nudges the same coalesced refetch (B-1341 / B-1345).
 *   - Backstop: Apollo's `pollInterval` — a declarative missed-delivery poll Apollo owns (no
 *     hand-rolled timer/dispose), paused while the tab is hidden via `skipPollAttempt`.
 *   - Imperative: the provider's stream-finished `refetch`.
 */
import { useConversationHistoryChangedSubscription, useConversationMessagesQuery } from "@qw/graphql";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { deriveServerHistoryState, type ServerHistoryState } from "../server-history-state";

/** Collapse a burst of push wakeups (subscription redeliveries) into one refetch. */
const COALESCE_WINDOW_MS = 250;
/** Declarative fallback poll cadence — the missed-delivery backstop when the socket drops. */
const POLL_FALLBACK_MS = 30_000;

export interface UseConversationMessagesResult {
  /** Exhaustive source state — the provider must handle every non-`ready` case. */
  state: ServerHistoryState;
  /** Force a fresh whole-tree refetch (driven when the SDK finishes a real stream). */
  refetch: () => Promise<void>;
  /** Schedule a coalesced whole-tree refetch — the ambient freshness path (`useConversationFreshness`
   *  drives this on tab-return; the push subscription uses it internally). */
  scheduleRefetch: () => void;
}

export function useConversationMessages(rootChatSessionId: string): UseConversationMessagesResult {
  const { data, error, refetch } = useConversationMessagesQuery({
    variables: { rootChatSessionId },
    // Fast first paint from cache, fresh from network; the provider's completion-driven
    // `refetch` forces a network read so the slot's drop predicate sees persisted rows.
    fetchPolicy: "cache-and-network",
    // Declarative missed-delivery backstop owned by Apollo (no hand-rolled poll lifecycle). Apollo's
    // poll loop does NOT check tab visibility, so pause it explicitly while hidden — tab-return
    // nudges an immediate refetch below, and the subscription is the fast path regardless.
    pollInterval: POLL_FALLBACK_MS,
    skipPollAttempt: () => typeof document !== "undefined" && document.visibilityState === "hidden",
  });

  const state = useMemo(() => deriveServerHistoryState(data?.conversationMessages, Boolean(error)), [data, error]);

  // The push subscription must key on the same *effective root* the wakeup is published on. The
  // query canonicalizes a child session id to its root server-side and stamps that root on every
  // returned node, so read it back and subscribe on it. Without this, a child-keyed view (the
  // standalone viewer deep-linked to a child) would subscribe on the child id and never match the
  // root-scoped wakeup — silently falling back to the poll. Falls back to the raw arg until the
  // first result lands (an empty conversation has nothing to wake for meanwhile).
  const conversationMessages = data?.conversationMessages;
  const resolvedRootChatSessionId =
    (conversationMessages?.__typename === "ConversationMessages"
      ? conversationMessages.messages[0]?.rootSessionId
      : undefined) ?? rootChatSessionId;

  const coalesceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearCoalesceTimer = useCallback(() => {
    if (coalesceTimerRef.current !== null) {
      clearTimeout(coalesceTimerRef.current);
      coalesceTimerRef.current = null;
    }
  }, []);

  const refetchServerHistory = useCallback(async () => {
    // An imperative refetch supersedes any pending coalesced one (e.g. the stream-finished call
    // racing a just-scheduled wakeup) — cancel it so the two don't both fire.
    clearCoalesceTimer();
    await refetch();
  }, [refetch, clearCoalesceTimer]);

  // Mirror the latest `refetchServerHistory` into a ref so the coalesce scheduler stays stable
  // across its identity changes. Written in a commit-phase effect (not during render) so an
  // abandoned concurrent render can't publish an uncommitted closure.
  const refetchRef = useRef(refetchServerHistory);
  useEffect(() => {
    refetchRef.current = refetchServerHistory;
  }, [refetchServerHistory]);

  // Single coalesced refetch shared by the fire-and-forget triggers (push subscription, tab-return).
  // A hand-rolled schedule-once-per-window (not a library debounce), stable across renders (empty
  // deps), so the session-switch cleanup below runs ONLY on a real session change — not on every
  // render, which would otherwise cancel a pending wakeup mid-window and drop the live refresh.
  // Bounds latency to ~COALESCE_WINDOW_MS after the first trigger even under a continuous stream.
  // Errors surface through `state`, so swallow to avoid an unhandled rejection.
  const scheduleCoalescedRefetch = useCallback(() => {
    if (coalesceTimerRef.current !== null) {
      return;
    }
    coalesceTimerRef.current = setTimeout(() => {
      coalesceTimerRef.current = null;
      void refetchRef.current().catch(() => undefined);
    }, COALESCE_WINDOW_MS);
  }, []);

  // Clear the pending coalesce timer on unmount AND on a chat-session switch — `ChatProvider` reuses
  // this hook across sessions (no per-session key), so without keying this cleanup on
  // `rootChatSessionId` a wakeup scheduled for the previous session would refetch under the next
  // one. `scheduleCoalescedRefetch` is stable across renders, so this fires only on real changes.
  useEffect(() => clearCoalesceTimer, [rootChatSessionId, clearCoalesceTimer]);

  // Fast path: server push wakeup. Any change in the subtree nudges a coalesced refetch — this is
  // what surfaces an out-of-band child elicit/output live, without a stream finishing or a tab
  // switch. It is only ever a nudge; the refetched `conversationMessages` is the source of truth.
  useConversationHistoryChangedSubscription({
    variables: { rootChatSessionId: resolvedRootChatSessionId ?? "" },
    onData: () => scheduleCoalescedRefetch(),
  });

  // Tab-return recovery (B-1341 / B-1345) is now driven by the shared `useConversationFreshness`
  // controller (one visibility listener for both the history refetch and the stream re-attach),
  // via the `scheduleRefetch` returned below. The push subscription above still nudges directly.

  return {
    state,
    refetch: refetchServerHistory,
    scheduleRefetch: scheduleCoalescedRefetch,
  };
}
